import {
  ActionIcon,
  Button,
  LoadingOverlay,
  PinInput,
  Skeleton,
  UnstyledButton
} from "@marketplace/ui";
import { Link } from "@tanstack/react-router";
import { Controller } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { MdArrowBack } from "react-icons/md";

import { Card } from "@/components";

import { useVerifyOtpForm } from "../../hooks/forms/use-verify-otp-form";
import { useSendOtp } from "../../hooks/use-send-otp";
import { CountdownTimer } from "../countdown-timer";
import classes from "./otp-card.module.css";
import type { OtpCardProps } from "./otp-card.types";

export const OtpCard = ({ page }: OtpCardProps) => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("auth", {
    keyPrefix: "verifyOtp"
  });
  const {
    control,
    handleSubmit,
    isPending,
    errors,
    encryptionLoading,
    email,
    expiredAt
  } = useVerifyOtpForm();

  const { handleSendOtp, canSendOtp, timeLeft } = useSendOtp();

  return (
    <Card
      className="w-[412px] relative"
      title="Enter verification code"
      upperSection={
        <ActionIcon
          component={Link}
          to={page === "login" ? "/auth/login" : "/auth/register"}
        >
          <MdArrowBack />
        </ActionIcon>
      }
    >
      <LoadingOverlay visible={encryptionLoading} />
      <form className="flex flex-col gap-y-6" onSubmit={handleSubmit}>
        {!email && encryptionLoading && (
          <div className="flex flex-col gap-y-1">
            <Skeleton height={14} />
            <Skeleton height={14} width="50%" />
          </div>
        )}
        {email && (
          <p className="m-0 text-fjord-grey-400 text-p-s">
            {t("otpHasBeenSent", {
              email
            })}
          </p>
        )}
        <Controller
          name="otp"
          control={control}
          render={({ field }) => (
            <PinInput
              {...field}
              classNames={{
                pinInput: classes.pinInput,
                input: classes.input
              }}
              length={6}
              oneTimeCode
              type="number"
              error={errors.otp?.message}
            />
          )}
        />
        <div className="flex flex-col gap-1">
          <p className="m-0 text-fjord-grey-400 text-p-s">
            {t("didntGetTheCode")}{" "}
            {canSendOtp && !encryptionLoading && (
              <UnstyledButton
                onClick={handleSendOtp}
                className="text-p-s text-[#2D7FF9] font-semibold"
              >
                {t("resend")}
              </UnstyledButton>
            )}
            {expiredAt !== 0 && timeLeft > 0 && !encryptionLoading && (
              <CountdownTimer targetUnixTimestamp={expiredAt} />
            )}
          </p>
          <div className="pr-4 text-center">
            <p className="m-0 rounded-[3px] bg-mischka-100 text-fjord-grey-400 text-[10px]">
              <Trans
                t={t}
                i18nKey="otpNotInInbox"
                components={{
                  otp: <span key={"otp"} className="font-bold" />,
                  spam: <span key={"spam"} className="font-bold" />,
                  trash: <span key={"trash"} className="font-bold" />
                }}
              />
            </p>
          </div>
        </div>
        <Button type="submit" loading={isPending} fullWidth variant="primary">
          {tCommon("confirm")}
        </Button>
      </form>
    </Card>
  );
};
